import React, { useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import {
  Activity,
  ChevronDown,
  CircleDot,
  Glasses,
  MailOpen,
  Send,
  StickyNote,
} from "lucide-react";

const ICON_MAP = {
  "email.sent": {
    Icon: Send,
    wrap: "bg-sky-100 text-sky-600",
  },
  "email.inbound": {
    Icon: MailOpen,
    wrap: "bg-emerald-100 text-emerald-600",
  },
  impersonate: {
    Icon: Glasses,
    wrap: "bg-violet-100 text-violet-600",
  },
  "health.review": {
    Icon: Activity,
    wrap: "bg-orange-100 text-orange-600",
  },
  note: {
    Icon: StickyNote,
    wrap: "bg-amber-100 text-amber-700",
  },
  generic: {
    Icon: CircleDot,
    wrap: "bg-gray-100 text-gray-500",
  },
};

function activityKind(item) {
  const cat = `${item?.category || ""} ${item?.action || ""}`.toLowerCase();
  if (cat.includes("email.sent")) return "email.sent";
  if (cat.includes("email.inbound")) return "email.inbound";
  if (cat.includes("impersonate")) return "impersonate";
  if (cat.includes("health.review")) return "health.review";
  if (/(^|[\s.])note([\s.]|$)/.test(cat.trim())) return "note";
  return "generic";
}

function parseDate(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) {
    return new Date(n < 1e12 ? n * 1000 : n);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatCompactTs(value) {
  const d = parseDate(value);
  if (!d) return value == null || value === "" ? "" : String(value);
  const now = new Date();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (d.toDateString() === now.toDateString()) return time;
  const date = d.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
  return `${date} · ${time}`;
}

function ActivityItem({ item, expanded, onToggle, isLast }) {
  const kind = activityKind(item);
  const { Icon, wrap } = ICON_MAP[kind] || ICON_MAP.generic;
  const headline = item.summary || item.action || "Event";
  const actor = item.actorEmail || item.actorUserId;
  const hasDetails = Boolean(item.body || item.category || actor);

  return (
    <li className="relative flex gap-3">
      <div className="flex flex-col items-center w-8 shrink-0">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center ${wrap}`}
        >
          <Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
        </div>
        {!isLast && <div className="w-px flex-1 bg-gray-200 mt-1" />}
      </div>
      <button
        type="button"
        onClick={() => hasDetails && onToggle()}
        aria-expanded={expanded}
        className={`flex-1 min-w-0 text-left ${
          hasDetails ? "cursor-pointer group" : "cursor-default"
        } ${isLast ? "pb-0" : "pb-5"}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] text-gray-400">
              {formatCompactTs(item.timestamp)}
            </div>
            <div className="mt-0.5 text-[13px] font-semibold text-gray-900 leading-snug group-hover:text-gray-700">
              {headline}
            </div>
          </div>
          {hasDetails && (
            <ChevronDown
              className={`w-3.5 h-3.5 text-gray-300 mt-1 shrink-0 transition-transform ${
                expanded ? "rotate-180 text-gray-500" : "group-hover:text-gray-400"
              }`}
            />
          )}
        </div>
        {expanded && (
          <div className="mt-2 space-y-1.5">
            {(item.category || actor) && (
              <div className="text-[12px] text-gray-500">
                {[item.category, actor].filter(Boolean).join(" · ")}
              </div>
            )}
            {item.body && (
              <p className="text-[13px] text-gray-700 whitespace-pre-wrap max-h-56 overflow-y-auto rounded-md bg-gray-50 border border-gray-100 px-2.5 py-2">
                {item.body}
              </p>
            )}
          </div>
        )}
      </button>
    </li>
  );
}

const ActivityTimeline = ({
  activity,
  onAddNote,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}) => {
  const [expanded, setExpanded] = useState(() => new Set());

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <section className="bg-white border border-gray-200 rounded-[10px] p-4">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-[15px] font-semibold">Activity</h2>
        <Button
          onClick={onAddNote}
          sx={{
            textTransform: "none",
            border: "1px solid #E0E0E0",
            color: "#374151",
            borderRadius: "8px",
            px: 1.5,
          }}
        >
          Add note
        </Button>
      </div>
      {activity.length === 0 ? (
        <p className="text-[13px] text-gray-400">No activity yet</p>
      ) : (
        <>
          <ul>
            {activity.map((item, idx) => {
              const key =
                item.eventId || item.sk || `${item.timestamp}-${item.summary}-${idx}`;
              return (
                <ActivityItem
                  key={key}
                  item={item}
                  expanded={expanded.has(key)}
                  onToggle={() => toggle(key)}
                  isLast={idx === activity.length - 1 && !hasMore}
                />
              );
            })}
          </ul>
          {hasMore && (
            <div className="pt-1">
              <Button
                onClick={onLoadMore}
                disabled={loadingMore}
                fullWidth
                sx={{
                  textTransform: "none",
                  color: "#605BFF",
                  fontSize: "13px",
                  fontWeight: 600,
                  borderRadius: "8px",
                }}
              >
                {loadingMore ? (
                  <CircularProgress size={16} sx={{ color: "#605BFF" }} />
                ) : (
                  "Load more"
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default ActivityTimeline;
